import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin, Clock } from 'lucide-react';
import { companyInfo } from '../data/mockData';

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const lang = i18n.language || 'en';

  return (
    <footer id="contact" className="bg-gray-900 text-gray-300" data-testid="site-footer">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="md:col-span-2">
            <h3 className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent mb-4">
              {companyInfo.name}
            </h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {t('footer.company_description')}
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/people/Go-Vip-Portugal/61571543287074/" 
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 hover:bg-amber-600 p-3 rounded-full transition-colors duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/govipportugal/" 
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 hover:bg-amber-600 p-3 rounded-full transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/go-vip-portugal/" 
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 hover:bg-amber-600 p-3 rounded-full transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">{t('footer.contact_title')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <a href={`tel:${companyInfo.phone}`} className="hover:text-amber-400 transition-colors">
                    {companyInfo.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <a href={`mailto:${companyInfo.email}`} className="hover:text-amber-400 transition-colors">
                    {companyInfo.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span>Porto, Portugal</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">{t('footer.hours_title')}</h4>
            <ul className="space-y-3">
              <li className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <div>
                  <p className="text-sm">{t('footer.hours_days')}</p>
                  <p className="text-white font-medium">{t('footer.hours_24_7')}</p>
                </div>
              </li>
            </ul>
            <p className="text-sm text-gray-500 mt-4">
              {t('footer.hours_note')}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-500">
              © {currentYear} {companyInfo.name}. {t('footer.rights')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
              <Link to={`/${lang}/privacy`} className="text-gray-500 hover:text-amber-400 transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to={`/${lang}/terms`} className="text-gray-500 hover:text-amber-400 transition-colors">
                {t('footer.terms')}
              </Link>
              <Link to={`/${lang}/tours`} className="text-gray-500 hover:text-amber-400 transition-colors" data-testid="footer-tours-link">
                {t('footer.tours')}
              </Link>
              <Link to={`/${lang}/blog`} className="text-gray-500 hover:text-amber-400 transition-colors">
                Blog
              </Link>
              <a href="https://www.livroreclamacoes.pt" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-amber-400 transition-colors">
                {t('footer.complaints_book')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
